import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { apiFetch } from '@/lib/apiFetch'
import OnboardingProgress from '@/components/onboarding/OnboardingProgress'
import SetProfileStep from '@/components/onboarding/SetProfileStep'
import AddChildStep from '@/components/onboarding/AddChildStep'
import InviteStep from '@/components/onboarding/InviteStep'
import IncentiveStep from '@/components/onboarding/IncentiveStep'

interface ChildInfo {
  id: number
  displayName: string
  username: string
}

export default function OnboardingPage() {
  const { user, refresh } = useAuth()
  const history = useHistory()
  const [step, setStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set())
  const [children, setChildren] = useState<ChildInfo[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    (async () => {
      try {
        const status = await apiFetch('/onboarding/status')
        const completed = new Set<number>()
        let startStep = 1

        if (status.hasPassword && status.hasName) {
          completed.add(1)
          startStep = 2
        }
        if (status.childCount > 0) {
          completed.add(2)
          setChildren(status.children || [])
          if (startStep <= 2) startStep = 3
        }
        if (status.hasIncentive) {
          completed.add(4)
        }

        setCompletedSteps(completed)
        setStep(startStep)
      } catch {
        // If status fails, start from step 1
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const completeStep = (stepNum: number) => {
    setCompletedSteps(prev => new Set([...prev, stepNum]))
    setStep(stepNum + 1)
  }

  const finishOnboarding = async () => {
    await refresh()
    history.replace('/dashboard')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <OnboardingProgress currentStep={step} completedSteps={completedSteps} />

      <div className="transition-all duration-300">
        {step === 1 && (
          <SetProfileStep onComplete={() => completeStep(1)} />
        )}
        {step === 2 && (
          <AddChildStep
            existingChildren={children}
            onComplete={(newChildren) => {
              setChildren(newChildren)
              completeStep(2)
            }}
          />
        )}
        {step === 3 && (
          <InviteStep
            children={children}
            onComplete={() => completeStep(3)}
            onSkip={() => completeStep(3)}
          />
        )}
        {step === 4 && (
          <IncentiveStep
            children={children}
            onComplete={finishOnboarding}
            onSkip={finishOnboarding}
          />
        )}
        {step > 4 && (() => { finishOnboarding(); return null })()}
      </div>
    </div>
  )
}
